import React from 'react';
import { useTranslation } from 'react-i18next';
import Select from '../../../../components/ui/Select';
import Button from '../../../../components/ui/Button';
import Input from '../../../../components/ui/Input';
import Icon from '../../../../components/AppIcon';

interface Filters {
  department: string;
  departmentId?: number | null;
  payrollCycle: string;
  year: string;
  month: string;
  search: string;
}

interface FilterPanelProps {
  filters: Filters;
  onFilterChange: (filterName: keyof Filters, value: string) => void;
  departments?: string[];
  loadingDepartments?: boolean;
  availableYears?: string[];
  availableMonths?: { value: string; label: string }[];
}

const FilterPanel: React.FC<FilterPanelProps> = ({
  filters,
  onFilterChange,
  departments = [],
  loadingDepartments = false,
  availableMonths = []
}) => {
  const { t } = useTranslation();

  const years = [...Array(5)].map((_, i) => (new Date().getFullYear() - i).toString());

  const departmentOptions = [
    { value: '', label: t('All Departments') },
    ...departments.map(d => ({ value: d, label: d }))
  ];

  const yearOptions = years.map(y => ({ value: y, label: y }));

  const monthOptions = [
    { value: '', label: t('All Months') },
    ...availableMonths.map(m => ({ value: m.value, label: m.label }))
  ];

  const handleClearFilters = () => {
    onFilterChange('search', '');
    onFilterChange('department', '');
    onFilterChange('year', new Date().getFullYear().toString());
    onFilterChange('month', '');
  };

  return (
    <div className="mb-6" data-testid="filter-panel">
      <div className="flex items-center gap-3 mb-4 rtl:space-x-reverse">
        <Icon name="Filter" size={20} className="text-primary" />
        <h3 className="text-lg font-semibold text-foreground">
          {t('Filters')}
        </h3>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Department */}
        <Select
          data-testid="filter-control"
          label={t('Department')}
          options={departmentOptions}
          value={filters.department}
          onChange={(val) => onFilterChange('department', val as string)}
          disabled={loadingDepartments}
          loading={loadingDepartments}
          placeholder={loadingDepartments ? t('Loading departments...') : t('All Departments')}
        />

        {/* Year */}
        <Select
          data-testid="filter-control"
          label={t('Year')}
          options={yearOptions}
          value={filters.year}
          onChange={(val) => onFilterChange('year', val as string)}
        />

        {/* Month */}
        <Select
          data-testid="filter-control"
          label={t('Month')}
          options={monthOptions}
          value={filters.month || ''}
          onChange={(val) => onFilterChange('month', val as string)}
        />

        {/* Search */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-1.5">{t('Search')}</label>
          <div className="relative">
            <div className="absolute inset-y-0 start-0 flex items-center ps-3 pointer-events-none">
              <Icon name="Search" size={16} className="text-muted-foreground" />
            </div>
            <Input
              data-testid="search-input"
              placeholder={t('Name, ID, department...')}
              value={filters.search || ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => onFilterChange('search', e.target.value)}
              className="ps-9"
            />
          </div>
        </div>
      </div>

      <div className="flex items-end gap-2 mt-4">
        <Button
          variant="outline"
          onClick={handleClearFilters}
          iconName="X"
        >
          {t('Clear Filters')}
        </Button>
      </div>
    </div>
  );
};

export default FilterPanel;
